import { Global, css } from '@emotion/react';
import { Theme, useTheme } from '@mui/material';
import useMountEffect from '@shared/hooks/useMountEffect';
import { memo, useId } from 'react';
import { CSSInterpolation, CSSObject } from 'tss-react';

type Props<TTheme extends Theme = Theme> = Either<
  {
    styles?: CSSObject | ((theme: TTheme) => CSSObject);
  },
  {
    backgroundColor?: string | ((palette: TTheme['palette']) => string);
  }
>;
const GlobalStyles = ({ styles, backgroundColor }: Props) => {
  const theme = useTheme();
  const uid = useId();
  const mainStyles = typeof styles === 'function' ? styles(theme) : styles;
  const bgColor =
    typeof backgroundColor === 'function' ? backgroundColor(theme.palette) : backgroundColor;
  const processedStyles = {
    ...mainStyles,
    ...(bgColor ? { 'html:root': { backgroundColor: bgColor, content: `"${uid}"` } } : {}),
  } as CSSInterpolation;

  const compiledStyles = css(processedStyles);
  useMountEffect(() => () => {
    Array.from(
      document.head.querySelectorAll<HTMLStyleElement>('style[data-emotion$="-global"]'),
    ).forEach((style) => {
      if (
        style.textContent === compiledStyles.styles ||
        style.sheet?.cssRules[0]?.cssText.includes(`content: "${uid}"`)
      ) {
        style.remove();
      }
    });
  });

  return <Global styles={compiledStyles} />;
};

export type CustomGlobalStyles<TTheme extends Theme> = FC<Props<TTheme>>;

// GlobalStyles should only depend on the theme, hence the `memo`
// we should never use this export directly(use on a per theme basis), hence the `as never`
export default memo(GlobalStyles, () => true) as never;
